import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Link } from 'react-router-dom';
import axios from 'axios';

import useInput from '../../../hooks/useInput';
import Error from '../../common/Error';
import Textarea from '../../common/Textarea';
import Submit from '../../common/PrimaryButton';
import GeneralValidator from '../../../helpers/validators/endorsements/general.validator';
import findErrorField from '../../../helpers/serverError';
import AlertDispatch from '../../../contexts/alert.dispatch';
import alertActions from '../../../actions/alert.actions';

import style from './General.module.css';

const GeneralChange = React.memo(
  ({ clientId, effDate = '', policyIds, setEffDateError, endorsementType }) => {
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({
      description: '',
      effDate: '',
    });
    const description = useInput('');
    const alertDispatch = useContext(AlertDispatch);

    const getRequestName = () => {
      switch (endorsementType) {
        case 'PolicyLimitChange':
          return 'Policy Limit Change';
        case 'CoverageRetentionChange':
          return 'Coverage Retention Change';
        case 'DirectorsOfficersCoverageChange':
          return 'Add/Remove/Change Directors & Officers Coverage';
        case 'EmploymentPracticesCoverageChange':
          return 'Add/Remove/Change Employment Practices Coverage';
        case 'FiduciaryCoverageChange':
          return 'Add/Remove/Change Fiduciary Coverage';
        case 'CrimeCoverageChange':
          return 'Add/Remove/Change Crime Coverage';
        default:
          return '';
      }
    };

    const handleSubmit = event => {
      event.preventDefault();

      const validation = GeneralValidator.validate({
        description: description.value,
        effDate,
      });

      setErrors({
        description: validation.description.message,
      });

      setEffDateError(validation.effDate.message);

      const handleSuccess = () => {
        alertDispatch({
          type: alertActions.SUCCESS,
          message: `Thank you for your ${getRequestName()} request. A member of your dedicated team will respond within 1 business day.`,
        });
        setSubmitted(true);
      };

      const handleError = ({ response }) => {
        if (response && response.data && response.data.ValidationErrors) {
          const effDateError = findErrorField(
            response.data.ValidationErrors,
            'EffDate',
          );

          const descriptionError = findErrorField(
            response.data.ValidationErrors,
            'Description',
          );

          setEffDateError(effDateError ? effDateError.Messages[0] : '');

          setErrors({
            description: descriptionError ? descriptionError.Messages[0] : '',
          });
        } else {
          alertDispatch({
            type: alertActions.ERROR,
            message: 'Something went wrong. Please try again',
          });
          setSubmitted(true);
        }
      };

      if (validation.isValid) {
        axios
          .post(
            `${
              process.env.REACT_APP_BASE_URL
            }hub/clients/${clientId}/requests/endorsements/general?policyIds=${policyIds.join(
              '&policyIds=',
            )}`,
            {
              Description: description.value,
              EffDate: effDate,
              Type: endorsementType,
            },
            {
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
            },
          )
          .then(handleSuccess)
          .catch(handleError);
      }
    };

    return submitted ? (
      <Redirect to={`/clients/${clientId}/endrosements`} />
    ) : (
      <>
        <div className={`form-group ${style.info}`}>
          <div className={style.label}>Description of this Request:</div>
          <Textarea
            name="description"
            styles={{ minHeight: '90px' }}
            {...description}
          />
          <Error error={errors.description} />
        </div>
        <div className={style.actions}>
          <Submit
            type="submit"
            text="Submit Request"
            styles={{ width: '200px' }}
            onClick={handleSubmit}
          />
          <Link to={`/clients/${clientId}`} className={style.cancelBtn}>
            Cancel
          </Link>
        </div>
      </>
    );
  },
);

GeneralChange.propTypes = {
  clientId: PropTypes.string.isRequired,
  effDate: PropTypes.instanceOf(Date),
  policyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  setEffDateError: PropTypes.func.isRequired,
  endorsementType: PropTypes.string.isRequired,
};

export default GeneralChange;
